/**
 * ProfileBanner — full-width decorative banner for the profile page.
 *
 * Pure presentational: User in, JSX out. No hooks or stores.
 * Background gradient is built from the user's bannerTone via bannerGradient(),
 * which uses a fixed, accent-independent palette so identity stays stable.
 */
import styled from 'styled-components';
import { Icon } from '@/icons';
import { bannerGradient } from '@/theme/presets/bannerTones';
import type { User } from '@/core/models';
import { isHttpUrl } from '@/core/lib/isHttpUrl';
import { media } from '@/theme/media';

/* ── Styled components ──────────────────────────────────────────────────── */

const BannerRoot = styled.div<{ $tone?: string; $image?: string }>`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  background: ${({ $tone, $image }) =>
    $image
      ? `center / cover no-repeat url("${String($image).replace(/["\\]/g, '')}")`
      : bannerGradient($tone ?? 'violet')};
  flex-shrink: 0;

  /* Full-bleed on mobile: no rounded corners, flush edge-to-edge */
  ${media.mobile`height: 160px; border-radius: 0;`}
`;

/** Subtle glass-tinted vignette overlay — darkens edges for depth */
const Vignette = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, transparent 40%, rgba(0, 0, 0, 0.28) 100%);
  pointer-events: none;
`;

/** Decorative signal motif — large, low-opacity, absolutely positioned.
 *  Color is derived from textPrimary so it reads in both light and dark themes. */
const SignalDecor = styled.span`
  position: absolute;
  right: -16px;
  bottom: -20px;
  opacity: 0.18;
  pointer-events: none;
  color: ${({ theme }) => theme.color.textPrimary};
  /* Scale up the icon via transform without adding layout width */
  transform: scale(3.2);
  transform-origin: bottom right;
`;

/* ── Component ──────────────────────────────────────────────────────────── */

export interface ProfileBannerProps {
  user: User;
}

export function ProfileBanner({ user }: ProfileBannerProps) {
  const raw = user.banner ?? user.bannerTone ?? 'violet';
  const isUrl = isHttpUrl(raw);

  return (
    <BannerRoot
      $tone={isUrl ? undefined : raw}
      $image={isUrl ? raw : undefined}
      role="img"
      aria-label={`${user.name} profile banner`}
    >
      <SignalDecor aria-hidden>
        <Icon name="signal" size={64} />
      </SignalDecor>
      <Vignette aria-hidden />
    </BannerRoot>
  );
}
